/**
 * HMO Detection Utility
 * Detects which HMO portal the application is running in
 */

import { HMO, HMO_DOMAINS, HMOType } from "constants/hmo";

/**
 * Checks if the application is running in development mode
 * @returns true if in development, false otherwise
 */
function isDevelopment(): boolean {
  return import.meta.env.DEV === true || import.meta.env.MODE === 'development';
}

/**
 * Detects HMO from domain appearing anywhere in the URL string (development only)
 * Checks the entire URL string (path, search params, hash) for HMO domain patterns
 * @param url - The full URL string to check
 * @returns HMO identifier or null if no match
 */
function getHmoFromUrlString(url: string): HMOType {
  if (!url || !isDevelopment()) return null;

  const urlLower = url.toLowerCase();

  // Check each HMO's domain in the entire URL string
  for (const [hmo, domain] of Object.entries(HMO_DOMAINS)) {
    if (urlLower.includes(domain.toLowerCase())) {
      return hmo as HMO;
    }
  }

  return null;
}

/**
 * Extracts HMO identifier from a URL by checking the hostname
 * Uses HMO enum values directly for production/AWS environments
 * @param url - The URL to check
 * @returns HMO identifier or null if no match
 */
function getHmoFromUrl(url: string): HMOType {
  if (!url) return null;

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if hostname contains any HMO enum value
    for (const hmo of Object.values(HMO)) {
      if (hostname.includes(hmo)) {
        return hmo as HMO;
      }
    }
  } catch (e) {
    // Invalid URL, return null
    return null;
  }

  return null;
}


// Module-level cache - ensures single execution (React 19 best practice)
let cachedHmo: HMOType | undefined = undefined;

/**
 * Detects the HMO identifier based on various sources
 * Detection runs only once - subsequent calls return cached value
 * @returns HMO identifier or null for default theme
 */
export function detectHmo(): HMOType {
  // Return cached value if already detected (React 19 best practice)
  if (cachedHmo !== undefined) {
    return cachedHmo;
  }

  // Detection logic (runs only on first call)
  let detected: HMOType = null;

  // Development simulation: Check if HMO domain exists anywhere in URL string
  // This allows testing different HMO themes by including domain in URL (path, params)
  if (isDevelopment() && typeof window !== 'undefined' && window.location.href) {
    detected = getHmoFromUrlString(window.location.href);
  }

  if (!detected && typeof window !== 'undefined' && window.location.href) {
    detected = getHmoFromUrl(window.location.href);
  }

  // Cache the result (even if null)
  cachedHmo = detected;
  return cachedHmo;
}

/**
 * Gets the cached HMO value without triggering detection
 * Useful for accessing HMO in components without re-detection
 * @returns Cached HMO identifier or undefined if not yet detected
 */
export function getCachedHmo(): HMOType | undefined {
  return cachedHmo;
}

/**
 * Resets the cached HMO (mainly for testing)
 * @internal
 */
export function resetHmoCache(): void {
  cachedHmo = undefined;
}

